import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Calendar, Clock, ArrowRight, Phone } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import WhatsAppButton from "@/components/WhatsAppButton";
import SEOHead from "@/components/SEOHead";
import { Button } from "@/components/ui/button";
import { blogPosts } from "@/data/blogPosts";

const BlogArticlePage = () => {
  const { slug } = useParams<{ slug: string }>();
  const post = blogPosts.find((p) => p.slug === slug);

  if (!post) return <Navigate to="/blog" replace />;

  const related = blogPosts.filter((p) => p.slug !== slug).slice(0, 3);

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": post.seoTitle,
    "description": post.metaDescription,
    "image": post.image,
    "datePublished": post.date,
    "author": {
      "@type": "Organization",
      "name": "حسن عبدالله للمقاولات والديكورات",
      "url": "https://decoriyaa.com"
    },
    "publisher": {
      "@type": "Organization",
      "name": "حسن عبدالله للمقاولات والديكورات",
      "url": "https://decoriyaa.com"
    }
  };

  return (
    <div className="min-h-screen bg-background" dir="rtl">
      <SEOHead
        title={post.seoTitle}
        description={post.metaDescription}
        canonical={`/blog/${post.slug}`}
        schema={articleSchema}
        ogType="article"
        ogImage={post.image}
      />
      <Header />

      <main className="pt-24 pb-16">
        {/* Hero Image */}
        <div className="relative h-[300px] md:h-[400px] overflow-hidden">
          <img
            src={post.image}
            alt={post.imageAlt}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/40 to-transparent" />
        </div>

        <article className="container mx-auto px-4 -mt-20 relative z-10 max-w-3xl">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-sm text-muted-foreground mb-6">
            <Link to="/" className="hover:text-primary transition-colors">الرئيسية</Link>
            <span>/</span>
            <Link to="/blog" className="hover:text-primary transition-colors">المدونة</Link>
            <span>/</span>
            <span className="text-foreground">{post.category}</span>
          </nav>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-card rounded-2xl p-6 md:p-10 shadow-lg border border-border"
          >
            {/* Meta */}
            <div className="flex items-center gap-4 text-sm text-muted-foreground mb-4">
              <span className="bg-primary/10 text-primary px-3 py-1 rounded-full text-xs font-semibold">
                {post.category}
              </span>
              <span className="flex items-center gap-1">
                <Calendar className="w-3.5 h-3.5" />
                {new Date(post.date).toLocaleDateString("ar-SA")}
              </span>
            </div>

            <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-8 leading-relaxed">
              {post.title}
            </h1>

            {/* Content */}
            <div className="prose prose-lg max-w-none">
              {post.content.map((section, i) => {
                switch (section.type) {
                  case "paragraph":
                    return (
                      <p key={i} className="text-muted-foreground leading-relaxed mb-5">
                        {section.text}
                      </p>
                    );
                  case "heading":
                    return section.level === 3 ? (
                      <h3 key={i} className="text-lg font-bold text-foreground mt-6 mb-3">
                        {section.text}
                      </h3>
                    ) : (
                      <h2 key={i} className="text-xl font-bold text-foreground mt-8 mb-4 border-r-4 border-primary pr-3">
                        {section.text}
                      </h2>
                    );
                  case "list":
                    return (
                      <ul key={i} className="space-y-2 mb-6 mr-4">
                        {section.items?.map((item, j) => (
                          <li key={j} className="flex items-start gap-2 text-muted-foreground">
                            <span className="w-1.5 h-1.5 rounded-full bg-primary mt-2.5 shrink-0" />
                            {item}
                          </li>
                        ))}
                      </ul>
                    );
                  default:
                    return null;
                }
              })}
            </div>

            {/* Internal link to service */}
            <div className="mt-8 p-5 bg-muted rounded-xl border border-border">
              <p className="text-foreground font-semibold mb-2">
                تبحث عن تنفيذ احترافي؟
              </p>
              <p className="text-muted-foreground text-sm mb-4">
                فريقنا جاهز لتحويل أفكارك إلى واقع بأعلى جودة وأفضل الأسعار في الرياض
              </p>
              <Link to={post.relatedService}>
                <Button variant="outline" className="gap-2">
                  تعرف على الخدمة
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
            </div>

            {/* CTA */}
            <div className="mt-8 p-6 bg-secondary rounded-xl text-center">
              <h2 className="text-xl font-bold text-secondary-foreground mb-2">
                جاهز لتنفيذ مشروعك؟
              </h2>
              <p className="text-muted-foreground text-sm mb-5">
                تواصل معنا الآن واحصل على استشارة مجانية وعرض سعر
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <Button asChild className="gap-2 w-full sm:w-auto">
                  <a href="tel:0556641963">
                    <Phone className="w-4 h-4" />
                    اتصل الآن
                  </a>
                </Button>
                <Button asChild variant="outline" className="gap-2 w-full sm:w-auto">
                  <Link to="/contact">
                    اطلب الخدمة
                  </Link>
                </Button>
              </div>
            </div>
          </motion.div>

          {/* Related Articles */}
          {related.length > 0 && (
            <section className="mt-12">
              <h2 className="text-xl font-bold text-foreground mb-6">مقالات ذات صلة</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {related.map((r) => (
                  <Link
                    key={r.slug}
                    to={`/blog/${r.slug}`}
                    className="group bg-card rounded-xl overflow-hidden border border-border hover:border-primary/40 hover:shadow-md transition-all"
                  >
                    <img
                      src={r.image}
                      alt={r.imageAlt}
                      loading="lazy"
                      className="w-full h-36 object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="p-4">
                      <h3 className="text-sm font-bold text-foreground group-hover:text-primary transition-colors line-clamp-2">
                        {r.title}
                      </h3>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </article>
      </main>

      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default BlogArticlePage;
